import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
  SidebarInset,
  SidebarSeparator,
} from "@/components/ui/sidebar";
import { Home, Briefcase, User, BookOpen, Mail, Menu, HeartHandshake } from "lucide-react";

interface NavigationProps {
  children: React.ReactNode;
}

const Navigation = ({ children }: NavigationProps) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeSection, setActiveSection] = useState("home");
  const hostname = typeof window !== "undefined" ? window.location.hostname.toLowerCase() : "";
  const isEnglishSite =
    hostname.includes("careandtherapy.com") ||
    (!hostname.includes("omsorgsterapi.dk") && i18n.resolvedLanguage?.startsWith("en"));
  const supportLinkEn = import.meta.env.VITE_STRIPE_SUPPORT_LINK_EN?.trim() || "";
  const supportLinkDa = import.meta.env.VITE_STRIPE_SUPPORT_LINK_DA?.trim() || "";
  const supportLink = isEnglishSite
    ? supportLinkEn || supportLinkDa
    : supportLinkDa || supportLinkEn;

  // Main page navigation items (scroll-based)
  const mainPageItems = [
    {
      id: "home",
      label: t('navigation.home'),
      icon: Home,
      href: "#intro",
      type: "scroll"
    },
    {
      id: "services",
      label: t('navigation.services'),
      icon: Briefcase,
      href: "#services",
      type: "scroll"
    },
    {
      id: "about",
      label: t('navigation.about'),
      icon: User,
      href: "#om-omsorgsterapi",
      type: "scroll"
    },
    {
      id: "contact",
      label: t('navigation.contact'),
      icon: Mail,
      href: "#contact",
      type: "scroll"
    }
  ];

  // Page navigation items (route-based)
  const pageNavigationItems = [
    {
      id: "publications",
      label: t('navigation.publications'),
      icon: BookOpen,
      href: "/publications",
      type: "navigate"
    },
    {
      id: "therapists",
      label: t('navigation.therapists'),
      icon: User,
      href: "/therapists",
      type: "navigate"
    }
  ];

  useEffect(() => {
    // Set active section based on current route
    if (location.pathname === "/publications") {
      setActiveSection("publications");
      return;
    }
    if (location.pathname === "/therapists") {
      setActiveSection("therapists");
      return;
    }

    // Only handle scroll detection on main page
    if (location.pathname === "/") {
      const handleScroll = () => {
        const sections = ["intro", "om-omsorgsterapi", "services", "blog", "contact"];
        const scrollPosition = window.scrollY + 100;

        // Define which sections have corresponding navigation items
        const navSections = ["intro", "om-omsorgsterapi", "services", "contact"];
        let currentActiveSection = null;

        for (const section of sections) {
          const element = document.getElementById(section);
          if (element) {
            const offsetTop = element.offsetTop;
            const offsetBottom = offsetTop + element.offsetHeight;

            if (scrollPosition >= offsetTop && scrollPosition < offsetBottom) {
              // Only update active section if this section has a corresponding nav item
              if (navSections.includes(section)) {
                let mappedSection = section;
                if (section === "intro") mappedSection = "home";
                else if (section === "om-omsorgsterapi") mappedSection = "about";
                // contact, services remain as they are

                currentActiveSection = mappedSection;
              }
              break;
            }
          }
        }

        // Only update if we found a valid navigation section
        if (currentActiveSection) {
          setActiveSection(currentActiveSection);
        }
      };

      window.addEventListener("scroll", handleScroll);
      handleScroll(); // Initial check

      return () => window.removeEventListener("scroll", handleScroll);
    }
  }, [location.pathname]);

  const handleNavClick = (href: string, id: string, type: string) => {
    if (type === "navigate") {
      // Handle route navigation
      navigate(href);
      setActiveSection(id);
    } else {
      // Handle scroll navigation (only works on main page)
      if (location.pathname !== "/") {
        // If not on main page, navigate to main page first
        navigate("/");
        // Wait for navigation to complete, then scroll
        setTimeout(() => {
          const targetId = href.replace("#", "");
          const element = document.getElementById(targetId);
          if (element) {
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setActiveSection(id);
          }
        }, 100);
      } else {
        // Already on main page, just scroll
        const targetId = href.replace("#", "");
        const element = document.getElementById(targetId);
        if (element) {
          element.scrollIntoView({
            behavior: "smooth",
            block: "start"
          });
          setActiveSection(id);
        }
      }
    }
  };

  return (
    <SidebarProvider>
      <Sidebar className="border-r border-gray-200 bg-white w-56">
        <SidebarHeader className="border-b border-gray-200 p-4 bg-white">
          <div className="flex items-center gap-3">
            <img
              src="/images/omsorgsterapi-logo.png"
              alt="Logo"
              className="w-8 h-8 rounded-full"
            />
            <span className="font-semibold text-[#006B5F] text-sm">
              {t('intro.title')}
            </span>
          </div>
        </SidebarHeader>

        <SidebarContent className="p-4 bg-white">
          {/* Main Page Navigation Section */}
          <SidebarMenu>
            {mainPageItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeSection === item.id;

              return (
                <SidebarMenuItem key={item.id}>
                  <SidebarMenuButton
                    onClick={() => handleNavClick(item.href, item.id, item.type)}
                    className={`w-full justify-start gap-3 px-3 py-2 rounded-lg transition-colors ${isActive
                        ? "bg-[#006B5F] text-white hover:bg-[#005a50]"
                        : "text-gray-700 hover:bg-gray-100"
                      }`}
                  >
                    <Icon className="h-4 w-4" />
                    <span className="text-sm font-medium">{item.label}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              );
            })}
          </SidebarMenu>

          {/* Beautiful separator line */}
          <div className="my-4">
            <SidebarSeparator className="bg-[#006B5F]/20" />
          </div>

          {/* Page Navigation Section */}
          <SidebarMenu>
            {pageNavigationItems.map((item) => {
              const Icon = item.icon;
              const isActive = activeSection === item.id;

              return (
                <SidebarMenuItem key={item.id}>
                  <SidebarMenuButton
                    onClick={() => handleNavClick(item.href, item.id, item.type)}
                    className={`w-full justify-start gap-3 px-3 py-2 rounded-lg transition-colors ${isActive
                        ? "bg-[#006B5F] text-white hover:bg-[#005a50]"
                        : "text-gray-700 hover:bg-gray-100"
                      }`}
                  >
                    <Icon className="h-4 w-4" />
                    <span className="text-sm font-medium">{item.label}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              );
            })}
          </SidebarMenu>
        </SidebarContent>

        {supportLink && (
          <SidebarFooter className="border-t border-[#006B5F]/15 bg-white p-4">
            <a
              href={supportLink}
              target="_blank"
              rel="noopener noreferrer"
              className="block rounded-xl border border-[#006B5F]/15 bg-[#EEF7F5] p-4 transition-colors hover:bg-[#E5F2EF]"
            >
              <div className="flex items-start gap-3">
                <div className="rounded-lg bg-[#006B5F] p-2 text-white">
                  <HeartHandshake className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-[#006B5F]">
                    {t("booking.supportWork")}
                  </p>
                  <p className="mt-1 text-xs leading-5 text-[#4E6764]">
                    {t("booking.supportWorkDescription")}
                  </p>
                </div>
              </div>
            </a>
          </SidebarFooter>
        )}
      </Sidebar>

      <SidebarInset className="flex-1">
        {/* Mobile trigger button */}
        <div className="md:hidden fixed top-4 left-4 z-50">
          <SidebarTrigger className="bg-white shadow-lg border border-gray-200 rounded-lg p-2">
            <Menu className="h-5 w-5 text-[#006B5F]" />
          </SidebarTrigger>
        </div>

        {children}
      </SidebarInset>
    </SidebarProvider>
  );
};

export default Navigation;
